<template>
  <base-sheet :visible="visible" @close="$emit('close')">
  <h3 class="sheet-title">Configurações de download</h3>

  <button class="row clickable" type="button" @click="$emit('edit-quality')">
    <span class="row-text">
      <span class="row-label">Qualidade padrão</span>
      <span class="row-hint">Usada em Wi-Fi. Em rede móvel, pergunta a cada fila.</span>
    </span>
    <span class="row-value">
      {{ qualityLabel }}
      <i class="material-icons chevron">chevron_right</i>
    </span>
  </button>

  <button
    class="row clickable"
    type="button"
    role="switch"
    :aria-checked="playbackPriority ? 'true' : 'false'"
    @click="playbackPriority = !playbackPriority"
  >
    <span class="row-text">
      <span class="row-label">Priorizar reprodução</span>
      <span class="row-hint">Enquanto você assiste online, os downloads continuam mais devagar para não travar o vídeo.</span>
    </span>
    <span class="toggle" :class="{ on: playbackPriority }" aria-hidden="true">
      <span class="knob"></span>
    </span>
  </button>

  <button v-if="$isInternalUser" class="row clickable" type="button" @click="$emit('open-diagnostics')">
    <span class="row-text">
      <span class="row-label">Diagnóstico de downloads</span>
      <span class="row-hint">Histórico de tempo e velocidade dos downloads recentes.</span>
    </span>
    <span class="row-value">
      <i class="material-icons chevron">chevron_right</i>
    </span>
  </button>

  <button
    v-if="$isInternalUser"
    class="row clickable"
    type="button"
    role="switch"
    :aria-checked="showSyncPanel ? 'true' : 'false'"
    @click="showSyncPanel = !showSyncPanel"
  >
    <span class="row-text">
      <span class="row-label">Diagnóstico de lease e sincronização</span>
      <span class="row-hint">Mostra os painéis de lease (Meus Downloads) e da fila de eventos offline aguardando envio ao servidor.</span>
    </span>
    <span class="toggle" :class="{ on: showSyncPanel }" aria-hidden="true">
      <span class="knob"></span>
    </span>
  </button>

  <button class="action-btn cancel" type="button" @click="$emit('close')">Fechar</button>
  </base-sheet>
</template>

<script>
import BaseSheet from '@/components/base/BaseSheet';

// Bottom sheet "Configurações de download" — aberto pela engrenagem no header
// de Meus Downloads. Hospeda: (a) seleção de qualidade padrão e (b) atalho pro
// diagnóstico. O "Espaço usado" agora vive expandido no card "Conteúdo offline"
// do header da página, não aqui — fica mais discoverable.
const QUALITY_LABELS = { low: 'Baixa', medium: 'Média', high: 'Alta' };

export default {
  name: 'DownloadSettingsSheet',
  components: { BaseSheet },
  props: {
    visible: { type: Boolean, default: false },
    currentQuality: { type: String, default: null }
  },
  computed: {
    qualityLabel() {
      if (!this.currentQuality) return 'Não definida';
      return QUALITY_LABELS[this.currentQuality] || '—';
    },
    showSyncPanel: {
      get() {
        return this.$store.getters['download/getShowOfflineSyncPanel'];
      },
      set(value) {
        this.$store.commit('download/SET_SHOW_OFFLINE_SYNC_PANEL', value);
      }
    },
    playbackPriority: {
      get() {
        return this.$store.getters['download/getPlaybackPriority'];
      },
      set(value) {
        this.$store.commit('download/SET_PLAYBACK_PRIORITY', value);
      }
    }
  },
  watch: {
    visible(now) {
      // Mesmo lock-scroll dos outros sheets pra evitar scroll por trás do
      // backdrop em iOS WebView.
      if (now) document.body.style.overflow = 'hidden';
      else document.body.style.overflow = '';
    }
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  }
};
</script>

<style scoped lang="scss">



.sheet-title {
  margin: 0 0 12px;
  padding: 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--md-text, #fff);
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px;
  background: var(--md-surface-elev, rgba(255, 255, 255, 0.04));
  border: none;
  border-radius: 12px;
  text-align: left;
  color: var(--md-text, #fff);

  &.clickable {
    cursor: pointer;
    transition: background 0.15s;
    &:active {
      background: var(--md-row-active, rgba(255, 255, 255, 0.07));
    }
  }
  // Linha informativa: sem hover/active feedback (não-clicável).
  &.informative {
    cursor: default;
  }
}

.row-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.row-label {
  font-size: 14px;
  font-weight: 600;
}

.row-hint {
  font-size: 12px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.55));
  line-height: 1.35;
}

.row-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.7));

  &.static {
    // Variante informativa — sem chevron, alinha texto direto.
    color: var(--md-text, #fff);
  }
}

.chevron {
  font-size: 18px;
}

.toggle {
  flex-shrink: 0;
  width: 44px;
  height: 26px;
  border-radius: 13px;
  background: var(--md-border-strong, rgba(255, 255, 255, 0.2));
  transition: background 0.18s;
  display: inline-flex;
  align-items: center;
  padding: 0 3px;

  .knob {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.18s;
  }

  &.on {
    background: var(--md-accent, #4caf50);
    .knob {
      transform: translateX(18px);
    }
  }
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &.cancel {
    margin-top: 6px;
    background: var(--md-surface-elev, rgba(255, 255, 255, 0.05));
    color: var(--md-text-dim, rgba(255, 255, 255, 0.7));
  }
}


</style>
